import bubbleImg from 'assets/icons/mapIcons/mapTypeIcons/bubble.png';
import choroplethImg from 'assets/icons/mapIcons/mapTypeIcons/choropleth.png';
import monochromeImg from 'assets/icons/mapIcons/mapTypeIcons/monochrome.png';
import Highcharts, {
  CSSObject,
  PlotSeriesDataLabelsOptions,
  Series,
  SeriesLegendItemClickEventObject,
  SeriesWordcloudOptions,
} from 'highcharts';
import cloneDeep from 'lodash.clonedeep';
import { MapType } from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { Widget, WidgetType } from 'types/widget.types';
import { shuffle } from 'utils/array';
import { fitToContainer } from 'utils/dom';
import { isDevelopment, isTest } from 'utils/environment';
import { AreaChartWidgetData } from 'widgets/ChartWidgets/Area/AreaChartWidget.types';
import {
  ALLOW_SINGLE_SERIES_CHARTS,
  DEFAULT_TEXT_STYLE,
  FORCE_SINGLE_SERIES_CHARTS,
  SINGLE_COLUMN_CHARTS,
} from 'widgets/ChartWidgets/ChartWidget.config';
import { HighchartsSeriesData, useHighchartsData } from 'widgets/ChartWidgets/ChartWidget.hooks';
import { ChartDataSeries, ChartWidgetData, GlobalChartOptions } from 'widgets/ChartWidgets/ChartWidget.types';
import {
  ENABLED_PATTERN_IDS,
  HighChartsWidgetTypes,
  HighchartsOptions,
  PatternId,
} from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommon.types';
import { getHighChartsFunnelConfig } from 'widgets/ChartWidgets/FunnelChartWidget/FunnelChartWidget.helpers';
import { getHighChartsScatterBubbleConfig } from 'widgets/ChartWidgets/ScatterBubbleChartWidget/ScatterBubbleChartWidget.helpers';
import { ScatterBubbleChartWidgetData } from 'widgets/ChartWidgets/ScatterBubbleChartWidget/ScatterBubbleChartWidget.types';
import { StackedAreaChartWidgetData } from 'widgets/ChartWidgets/StackedArea/StackedAreaChartWidget.types';
import { AllWidgetData } from 'widgets/Widget.types';
import { useWidget } from 'widgets/sdk';

import { parseStrictNumber } from '../../../utils/number';
import {
  DEFAULT_BORDER_COLOR as DEFAULT_BAR_CHART_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH as DEFAULT_BAR_CHART_BORDER_WIDTH,
  DEFAULT_MARGIN_RIGHT,
  DEFAULT_SPACING_RIGHT,
} from '../BarChartWidget/BarChartWidget.config';
import { CustomLegendRender, applyPatterns, formatDataLabel } from '../ChartWidget.helpers';
import {
  DEFAULT_BORDER_COLOR as DEFAULT_COLUMN_CHART_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH as DEFAULT_COLUMN_CHART_BORDER_WIDTH,
} from '../ColumnChartWidget/ColumnChartWidget.config';
import { getHighChartsComboConfig } from '../ComboChartWidget/ComboChartWidget.helpers';
import { ComboChartWidgetData } from '../ComboChartWidget/ComboChartWidget.types';
import { FunnelChartWidgetData } from '../FunnelChartWidget/FunnelChartWidget.types';
import { getHighChartsHalfPieConfig } from '../HalfPieChartWidget/HalfPieChartWidget.helpers';
import { getHighChartHistogramConfig } from '../HistogramWidget/HistogramChartWidget.helpers';
import { getHighChartsPieConfig } from '../PieChartWidget/PieChartWidget.helpers';
import {
  DEFAULT_WORD_DIRECTION,
  COLOR_STEPS as WORD_CLOUD_COLOR_STEPS,
  WORD_DIRECTION_CONFIG,
} from '../WordCloud/WordCloudWidget.config';
import { WordCloudWidgetData } from '../WordCloud/WordCloudWidget.types';
import {
  FONT_SIZE_MULTIPLIER,
  HIDDEN_AXIS_LINE_COLOR,
  HIDDEN_AXIS_LINE_WIDTH,
  ITEM_MARGIN_BOTTOM_MULTIPLIER,
} from './DataTableChartCommon.config';

/**
 * Returns an array of randomized pattern codes.
 *
 * @param patterns The object of patterns from where to get the random list.
 */
export const getRandomPatterns = (patterns: PatternId[] = [...ENABLED_PATTERN_IDS]): PatternId[] => {
  shuffle(patterns);

  return patterns;
};

const labelWidthCanvas = document.createElement('canvas');

/**
 * This function takes the text and it's styling and returns
 * the width of the text using the given styling.
 * @param text The text to measure the width of
 * @param font The styling in format of bold/normal + font size + font name
 * @return Returns the width of the text as an int
 *
 * PORTED from E1
 */
function getLabelWidth(text: string, font: string): number {
  try {
    const context = labelWidthCanvas.getContext('2d');

    if (context === null) {
      return 0;
    }

    context.font = font; // set the font to the version passed in
    const metrics = context.measureText(text); // measure the length of the text using the styling
    return metrics.width;
  } catch (err) {
    console.log('Error getting data label width.');
    return 0; // worst case, if we error, return a valid number to avoid crashing
  }
}

/**
 * This function takes in the labelData label settings, and
 * returns all the necessary styling for the canvas
 * to measure the width while being formatted
 * @param style the object with the label settings
 * @return the styling as a string for the canvas function
 *
 * PORTED from E1
 */
function getLabelStyling(style: CSSObject): string {
  return `${style.fontWeight ? 'bold' : ''} ${style.fontSize} ${style.fontFamily}`;
}

/**
 * Builds High Chart Options object
 * @param chartType
 */
const useHighChartOptions = (
  chartType: HighChartsWidgetTypes,
  { animation = false }: GlobalChartOptions,
): HighchartsOptions => {
  const widgetData = useWidget<ChartWidgetData>();

  const {
    generalOptions,
    legend,
    seriesData,
    seriesMeta,
    widthPx,
    heightPx,
    dataLabels: { enabled: enableDataLabels, style: dataLabelsStyle, prefix = '', suffix = '' },
    numberFormat: { precision, separator } = {},
    xAxis: {
      enableLine: enableXLine = true,
      enablelabels: enableXlabels = true,
      style: { lineColor, lineWidth },
    },
    yAxis: {
      enablelabels: enableYlabels = true,
      enableLine: enableYLine = true,
      title: { text: yAxisTitle = '' } = {},
    },
    xAxis,
    yAxis,
    grid,
  } = widgetData;

  const minValue = parseFloat(seriesMeta.minValue ?? '');
  const maxValue = parseFloat(seriesMeta.maxValue ?? '');
  const minXValue = parseFloat(seriesMeta.minXValue ?? '');
  const maxXValue = parseFloat(seriesMeta.maxXValue ?? '');

  const allowSingleSeries = ALLOW_SINGLE_SERIES_CHARTS.includes(chartType);
  const forceSingleSeries = FORCE_SINGLE_SERIES_CHARTS.includes(chartType);
  const isSingleColumnCharts = SINGLE_COLUMN_CHARTS.includes(chartType);

  const { categories, series, keyCustom, mostLengthyLabel } = useHighchartsData({
    seriesData,
    seriesMeta,
    allowSingleSeries,
    forceSingleSeries,
    disableRowHeader: isSingleColumnCharts,
  });

  const fontSize = parseStrictNumber(legend.labelStyle.fontSize);
  const symbolWidth = fontSize * FONT_SIZE_MULTIPLIER;
  const symbolHeight = fontSize * FONT_SIZE_MULTIPLIER;
  const itemMarginBottom = fontSize * ITEM_MARGIN_BOTTOM_MULTIPLIER; // ensure stacked up labels are spaced evenly

  const defaultOptions: HighchartsOptions = {
    chart: {
      width: widthPx,
      height: heightPx,
      backgroundColor: 'transparent',
      animation,
    },
    // Title not displayed for now
    title: {
      text: undefined,
    },
    legend: {
      enabled: legend.enabled,
      symbolWidth: symbolWidth,
      symbolHeight: symbolHeight,
      itemMarginBottom: itemMarginBottom,
      itemStyle: {
        ...DEFAULT_TEXT_STYLE,
        color: legend.labelStyle.color,
        fontSize: legend.labelStyle.fontSize,
        fontFamily: legend.labelStyle.fontFamily,
        fontWeight: legend.labelStyle.fontWeight,
        fontStyle: legend.labelStyle.fontStyle,
        textDecoration: legend.labelStyle.textDecoration,
      },
    },
    // TODO: https://www.highcharts.com/docs/accessibility/accessibility-module
    accessibility: { enabled: false },
    credits: { enabled: false },
    tooltip: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      title: {
        text: xAxis.title.text,
        style: {
          ...DEFAULT_TEXT_STYLE,
          color: xAxis.title.style.color,
          fontFamily: xAxis.title.style.fontFamily,
          fontSize: xAxis.title.style.fontSize,
          fontWeight: xAxis.title.style.fontWeight,
          fontStyle: xAxis.title.style.fontStyle,
          textDecoration: xAxis.title.style.textDecoration,
        },
      },
      lineColor: enableXLine ? lineColor : HIDDEN_AXIS_LINE_COLOR,
      lineWidth: enableXLine ? lineWidth : HIDDEN_AXIS_LINE_WIDTH,
      gridLineColor: grid.style.lineColor,
      labels: {
        enabled: enableXlabels,
        style: {
          ...DEFAULT_TEXT_STYLE,
          color: xAxis.labelStyle.color,
          fontSize: xAxis.labelStyle.fontSize,
          fontFamily: xAxis.labelStyle.fontFamily,
          fontWeight: xAxis.labelStyle.fontWeight,
          fontStyle: xAxis.labelStyle.fontStyle,
          textDecoration: xAxis.labelStyle.textDecoration,
        },
      },
      max: Number.isNaN(maxXValue) ? null : maxXValue,
      min: Number.isNaN(minXValue) ? null : minXValue,
    },
    yAxis: {
      title: {
        text: yAxisTitle,
        style: {
          ...DEFAULT_TEXT_STYLE,
          color: yAxis.title.style.color,
          fontFamily: yAxis.title.style.fontFamily,
          fontSize: yAxis.title.style.fontSize,
          fontWeight: yAxis.title.style.fontWeight,
          fontStyle: yAxis.title.style.fontStyle,
          textDecoration: yAxis.title.style.textDecoration,
        },
      },
      max: Number.isNaN(maxValue) ? null : maxValue,
      min: Number.isNaN(minValue) ? null : minValue,
      lineColor: enableYLine ? lineColor : HIDDEN_AXIS_LINE_COLOR,
      lineWidth: enableYLine ? lineWidth : HIDDEN_AXIS_LINE_WIDTH,
      gridLineColor: grid.style.lineColor,
      labels: {
        enabled: enableYlabels,
        style: {
          ...DEFAULT_TEXT_STYLE,
          color: yAxis.labelStyle.color,
          fontSize: yAxis.labelStyle.fontSize,
          fontFamily: yAxis.labelStyle.fontFamily,
          fontWeight: yAxis.labelStyle.fontWeight,
          fontStyle: yAxis.labelStyle.fontStyle,
          textDecoration: yAxis.labelStyle.textDecoration,
        },
        reserveSpace: true,
        overflow: 'justify',
      },
    },
    plotOptions: {
      area: {
        borderWidth: 0,
        animation,
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            opacity: 1,
          },
        },
        dataLabels: {
          crop: false,
          overflow: 'allow',
          allowOverlap: false,
        },
      },
      bar: {
        borderWidth: generalOptions?.borderWidth ?? DEFAULT_BAR_CHART_BORDER_WIDTH,
        borderColor: generalOptions?.borderColor ?? DEFAULT_BAR_CHART_BORDER_COLOR,
        borderRadius: 2,
        animation,
        states: {
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
          },
          inactive: {
            opacity: 1,
          },
        },
        dataLabels: {
          crop: false,
          overflow: 'allow',
          verticalAlign: 'middle',
          allowOverlap: false,
        },
      },
      column: {
        borderWidth: generalOptions?.borderWidth ?? DEFAULT_COLUMN_CHART_BORDER_WIDTH,
        borderColor: generalOptions?.borderColor ?? DEFAULT_COLUMN_CHART_BORDER_COLOR,
        borderRadius: 2,
        animation,
        states: {
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
          },
          inactive: {
            opacity: 1,
          },
        },
      },
      line: {
        animation,
        states: {
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
          },
          inactive: {
            opacity: 1,
          },
        },
        dataLabels: {
          crop: false,
          overflow: 'allow',
          allowOverlap: false,
        },
      },
      series: {
        dataLabels: {
          enabled: enableDataLabels,
          style: {
            ...DEFAULT_TEXT_STYLE,
            ...dataLabelsStyle,
          },
          formatter: function () {
            return formatDataLabel({ dataLabel: this.y, prefix, suffix, precision, separator });
          },
        },
        events: {
          legendItemClick: function (this: Series, event: SeriesLegendItemClickEventObject) {
            return event.preventDefault();
          },
        },
        enableMouseTracking: false,
        animation,
      },
    },
  };

  switch (chartType) {
    case WidgetType.PieChart:
      return getHighChartsPieConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.HalfPieChart:
      return getHighChartsHalfPieConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.LineChart:
      return getHighChartsLineConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.BarChart:
      return getHighChartsBarConfig(defaultOptions, widgetData, categories, series, keyCustom, mostLengthyLabel);
    case WidgetType.ColumnChart:
      return getHighChartsColumnConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.StackedBarChart:
      return getHighChartsStackedBarConfig(defaultOptions, widgetData, categories, series, keyCustom, mostLengthyLabel);
    case WidgetType.StackedColumnChart:
      return getHighChartsStackedColumnConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.AreaChart:
      return getHighChartsAreaConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.StackedAreaChart:
      return getHighChartsStackedAreaConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.WordCloudChart:
      return getHighChartsWordCloudConfig(defaultOptions, widgetData, categories, series, keyCustom);
    case WidgetType.HistogramChart:
      return getHighChartHistogramConfig(defaultOptions, widgetData, series, keyCustom);
    case WidgetType.ComboChart:
      return getHighChartsComboConfig(
        defaultOptions,
        widgetData as ComboChartWidgetData,
        categories,
        series,
        keyCustom,
      );
    case WidgetType.FunnelChart:
      return getHighChartsFunnelConfig(
        defaultOptions,
        widgetData as FunnelChartWidgetData,
        categories,
        series,
        keyCustom,
      );
    case WidgetType.ScatterBubbleChart:
      return getHighChartsScatterBubbleConfig(
        defaultOptions,
        widgetData as ScatterBubbleChartWidgetData,
        categories,
        series,
        keyCustom,
      );
    default:
      throw new Error('All chart widget should have their own config');
  }
};

/**
 *
 * @param defaultOptions
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 */
const getHighChartsAreaConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: AreaChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  options.chart = options.chart || {};
  options.chart.type = 'area';

  options.keyCustom = keyCustom;

  options.xAxis = (options.xAxis as Highcharts.XAxisOptions) || {};
  options.xAxis.categories = categories;

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    type: 'area',
    dashStyle: 'Solid',
  }));

  options.plotOptions = options.plotOptions ?? {};
  options.plotOptions.area = options.plotOptions.area || {};
  options.plotOptions.area.fillOpacity = 0.1;

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};

/**
 * This is a temporary placeholder for the Word Cloud widget.
 * @param defaultOptions
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 * @returns
 */
const getHighChartsWordCloudConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: WordCloudWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;

  options.chart = options.chart || {};
  options.chart.type = 'wordcloud';

  options.keyCustom = keyCustom;

  options.plotOptions = options.plotOptions ?? {};
  options.plotOptions.wordcloud = {
    colors: widgetData.seriesMeta.colors.slice(0, widgetData.seriesMeta.colorSteps ?? WORD_CLOUD_COLOR_STEPS),
    rotation: WORD_DIRECTION_CONFIG.get(widgetData.generalOptions.wordDirection ?? DEFAULT_WORD_DIRECTION),
  };

  const { fontFamily, fontWeight, fontStyle } = widgetData.dataLabels.style;

  const wordCloudSeries = series.map(({ data }) => ({
    data: data?.map((entry) => {
      const { name, y } = entry != null && typeof entry === 'object' ? entry : { name: entry, y: 0 };
      return { name: name ?? undefined, weight: y ?? undefined };
    }),
    style: { fontFamily, fontWeight, fontStyle },
    type: 'wordcloud',
    spiral: 'custom' as any,
    placementStrategy: 'custom' as any,
  })) as SeriesWordcloudOptions[];

  // TODO: It make no sense to have specific generate function in the file chartCommon.helper.
  // Need to move to wordcloud folder with helper
  //
  // If we define the number of data to be display, Sort by weight then slice the array
  const dataPointLimit = widgetData.seriesMeta?.dataCount;
  if (!!dataPointLimit && widgetData.seriesMeta.dataCount! < (wordCloudSeries[0].data?.length ?? 0)) {
    wordCloudSeries[0].data = wordCloudSeries[0].data?.sort((a, b) => {
      if (!Array.isArray(a) && !Array.isArray(b) && a.weight && b.weight) {
        return b.weight - a.weight;
      }

      if (isDevelopment) {
        throw new Error('The generated data is not in the expected SeriesWordcloudOptions[] format.');
      }

      return 0; // Should never happen
    });

    wordCloudSeries[0].data = wordCloudSeries[0].data?.slice(0, widgetData.seriesMeta.dataCount);
  }

  options.series = wordCloudSeries;

  return options;
};

/**
 *
 * @param defaultOptions
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 */
const getHighChartsStackedAreaConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: StackedAreaChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  options.chart = options.chart || {};
  options.chart.type = 'area';

  options.keyCustom = keyCustom;

  options.xAxis = (options.xAxis as Highcharts.XAxisOptions) || {};
  options.xAxis.categories = categories;

  options.plotOptions = options.plotOptions ?? {};
  options.plotOptions.area = {
    ...options.plotOptions.area,
    fillOpacity: 0.2,
    stacking: 'normal',
  };

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    type: 'area',
    dashStyle: 'Solid',
    fillColor: undefined,
  }));

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 * @param mostLengthyLabel
 */
const getHighChartsColumnConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  options.chart = options.chart || {};
  options.chart.type = 'column';

  options.chart.events = { render: CustomLegendRender };

  options.keyCustom = keyCustom;

  options.xAxis = (options.xAxis as Highcharts.XAxisOptions) || {};
  options.xAxis.categories = categories;

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    visible: true,
    state: { hover: { enabled: false } },
    type: 'column',
  }));

  options.plotOptions = options.plotOptions ?? {};
  options.plotOptions.column = options.plotOptions.column ?? {};
  options.plotOptions.column.dataLabels = {
    crop: false,
    // With justify: This removes long data labels that don't fit and would overlap axis tick value label, contrary to 'none' value. Also, it doesn't force long labels to be centered regarding the columns.
    // @ts-ignore // Required if we set 'none', for some reason they don't allow it in the typescript interface
    overflow: 'justify',
  };

  options.yAxis = {
    ...options.yAxis,
    tickmarkPlacement: 'on',
    startOnTick: false,
    endOnTick: false,
    maxPadding:
      parseInt((options.plotOptions.series?.dataLabels as PlotSeriesDataLabelsOptions).style?.fontSize || '') / 100, // This allows for labels of size up to 30px to not get cut in a small chart of 480x350
  };

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 * @param mostLengthyLabel
 */
const getHighChartsStackedColumnConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = getHighChartsColumnConfig(defaultOptions, widgetData, categories, series, keyCustom);

  options.plotOptions = options.plotOptions || {};
  options.plotOptions.column = options.plotOptions.column || {};
  options.plotOptions.column.stacking = 'normal';
  options.plotOptions.column.borderRadius = 0;

  return options;
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 * @param mostLengthyLabel
 */
const getHighChartsBarConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
  mostLengthyLabel: string,
) => {
  const options = defaultOptions;
  const { prefix, suffix } = widgetData.dataLabels;
  const { precision, separator } = widgetData.numberFormat ?? {};
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  const formattedDataLabel = formatDataLabel({ dataLabel: mostLengthyLabel, prefix, suffix, precision, separator });
  options.chart = options.chart || {};
  options.chart.spacingRight = DEFAULT_SPACING_RIGHT;
  options.chart.type = 'bar';
  options.chart.marginRight =
    DEFAULT_MARGIN_RIGHT + getLabelWidth(formattedDataLabel, getLabelStyling(widgetData.dataLabels.style)); // Labels are always true;

  options.chart.events = { render: CustomLegendRender };

  options.keyCustom = keyCustom;

  options.xAxis = {
    ...((options.xAxis as Highcharts.XAxisOptions) || {}),
    categories,
    tickmarkPlacement: 'on',
    endOnTick: false,
  };

  options.yAxis = {
    ...((options.yAxis as Highcharts.YAxisOptions) || {}),
    startOnTick: false,
    endOnTick: false,
    tickmarkPlacement: 'on',
    softMax: (options.yAxis as Highcharts.YAxisOptions).max || undefined,
  };

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    visible: true,
    state: { hover: { enabled: false } },
    type: 'bar',
  }));

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  swapXYAxisSettings(options);

  return options;
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 * @param mostLengthyLabel
 */
const getHighChartsStackedBarConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
  mostLengthyLabel: string,
) => {
  const options = getHighChartsBarConfig(defaultOptions, widgetData, categories, series, keyCustom, mostLengthyLabel);

  options.plotOptions = options.plotOptions || {};
  options.plotOptions.bar = options.plotOptions.bar || {};
  options.plotOptions.bar.stacking = 'normal';
  options.plotOptions.bar.borderRadius = 0;

  options.chart = options.chart || {};
  options.chart.marginRight = 0;
  options.chart.spacingRight = DEFAULT_SPACING_RIGHT;

  return options;
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 */
const getHighChartsLineConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  options.chart = options.chart || {};
  options.chart.type = 'line';

  options.keyCustom = keyCustom;

  options.xAxis = (options.xAxis as Highcharts.XAxisOptions) || {};
  options.xAxis.categories = categories;

  options.yAxis = (options.yAxis as Highcharts.YAxisOptions) || {};

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    visible: true,
    state: { hover: { enabled: false } },
    type: 'line',
    dashStyle: 'Solid',
  }));

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  // highcharts bug for line chart in the test environment
  if (isTest) {
    options.xAxis.tickAmount = 100;
    options.yAxis.tickAmount = 100;
  }

  return options;
};

/**
 * A helper function to swap some xAxis settings and the yAxis settings.
 *
 * On Bar related charts such as the bar chart and the stacked bar chart,
 * some xAxis setting and yAxis setting are swapped.
 * We believe xAxis is always the bottom one and the yAxis is the side one,
 * but highchart think that the caterory is the xAxis and the value is yAxis.
 * Therefore, on bar chart, highchart's xAxis is a vertical line, and our xAxis reffer to a horizontal line.
 *
 * @param {object} options HighchartsOptions
 * @returns HighchartsOptions with some x and y settings are swapped
 */
export const swapXYAxisSettings = (options: HighchartsOptions) => {
  // Type guard
  if (!options.xAxis || Array.isArray(options.xAxis) || !options.yAxis || Array.isArray(options.yAxis)) return options;

  /* Temporary disable some of the swapping as we need some discussion */
  // const tempAxisTitle = options.xAxis.title;
  const tempAxisLabels = options.xAxis.labels;
  // const tempAxisLineColor = options.xAxis.lineColor;
  // const tempAxisLineWidth = options.xAxis.lineWidth;

  // options.xAxis.title = options.yAxis.title;
  options.xAxis.labels = options.yAxis.labels as Highcharts.XAxisOptions['labels'];
  // options.xAxis.lineColor = options.yAxis.lineColor;
  // options.xAxis.lineWidth = options.yAxis.lineWidth;

  // options.yAxis.title = tempAxisTitle;
  options.yAxis.labels = tempAxisLabels;
  // options.yAxis.lineColor = tempAxisLineColor;
  // options.yAxis.lineWidth = tempAxisLineWidth;

  return options;
};

const getMapTypeButtonImage = (mapType: MapType) => {
  switch (mapType) {
    case MapType.Bubble:
      return bubbleImg;
    case MapType.Choropleth:
      return choroplethImg;
    case MapType.Monochrome:
      return monochromeImg;
    default:
      break;
  }
};

/**
 * Calculates the dimensions to be assigned to widget in order for it to fit within the container
 * Used mostly in cases where a preview widget is a clone of an existing widget on the editor to track any changes
 *
 *
 * @param {Widget|AllWidgetData} widgetData - widget data of preview widget
 * @param {HTMLElement} containerEl - container element where the widget is rendered in
 * @returns An object containing the width and height dimensions - { width: number, height: number }
 */
const fitWidgetToContainer = (
  widgetData: Widget | AllWidgetData | { widthPx: number; heightPx: number },
  containerEl: HTMLElement,
) => {
  // containerEl is the element which we want to fit the widget in, should be a parent of the widget element
  if (!containerEl) return;
  // add padding to side of the container so less chance of clipping
  const PADDING_OFFSET = 10;
  const { offsetWidth, offsetHeight } = containerEl;
  const { widthPx, heightPx } = widgetData;
  const { width, height } = fitToContainer(offsetWidth, offsetHeight, widthPx, heightPx, PADDING_OFFSET);

  // return calculated dimension values
  return { width: width, height: height };
};

/**
 * Checks if series data is multiple or single series chart data
 *
 * @param {ChartDataSeries} seriesData
 * @returns {boolean}
 */
const isMultiSeriesChart = (seriesData: ChartDataSeries): boolean =>
  seriesData.map((data, index) => (index === 0 ? null : data.name)).filter((name) => !!name).length > 1;

export { useHighChartOptions, getMapTypeButtonImage, fitWidgetToContainer, isMultiSeriesChart };
